import { poolFor } from './facts';
import type { FactId, Operation, Tile } from './facts';
import { shuffle } from './rng';
import type { Rng } from './rng';

export interface BucketState {
  /** Текущая перестановка корзины. */
  order: FactId[];
  /** Сколько уже выдано из текущего круга. */
  cursor: number;
}

/** Ключ — пара «операция и плитка»: раздача идёт по корзинам, а не из
 *  общего пула (раздел 5.1). */
export type DealerState = Record<string, BucketState>;

/** Сравнения получают отдельный курсор (`'compare'`) от зачётных слотов
 *  (`'main'`): раздел 5.6 пропускает левые выражения сравнений через тот
 *  же раздатчик, а раздел 6.2 считает равномерность появлений так, будто
 *  их там нет. Общий курсор делает то, какие именно выдачи попадут в
 *  зачётные слоты, снова случайным — покрытие перестаёт быть равномерным
 *  (раздел 5.1, «длина дуги» в разделе 6.2). */
export type DealStream = 'main' | 'compare';

export function bucketKey(op: Operation, tile: Tile, stream: DealStream = 'main'): string {
  return stream === 'main' ? `${op}:${tile}` : `${stream}:${op}:${tile}`;
}

/** Новая перестановка круга. Первый элемент не совпадает с последним
 *  выданным, иначе стык кругов даёт соседний дубль (раздел 5.1). */
export function reshuffle(ids: readonly FactId[], previousLast: FactId | null, rng: Rng): FactId[] {
  if (ids.length < 2) return [...ids];
  const candidate = shuffle(ids, rng);
  if (candidate[0] !== previousLast) return candidate;
  // Запрещённый элемент встал первым — меняем его местами с последним.
  // Элементы корзины уникальны, поэтому последний заведомо не previousLast.
  const last = candidate.length - 1;
  [candidate[0], candidate[last]] = [candidate[last], candidate[0]];
  return candidate;
}

export function dealOne(
  state: DealerState,
  op: Operation,
  tile: Tile,
  rng: Rng,
  stream: DealStream = 'main',
): { factId: FactId; state: DealerState } {
  const key = bucketKey(op, tile, stream);
  const ids = poolFor([tile], op).map((fact) => fact.id);
  let bucket = state?.[key];

  /** Состояние приходит из IndexedDB и может быть испорчено. Проверяется не
   *  только курсор, но и состав: корзина обязана содержать ровно факты своей
   *  пары «операция и плитка». Иначе чужой факт улетал бы в `buildChoice4`,
   *  где он не принадлежит пулу, и запуск тренажёра падал бы насмерть вместо
   *  отката к свежей перестановке. */
  const expected = new Set(ids);
  const orderUsable =
    Array.isArray(bucket?.order) &&
    bucket.order.length === ids.length &&
    bucket.order.every((id) => expected.has(id));
  const cursorUsable =
    orderUsable &&
    Number.isInteger(bucket!.cursor) &&
    bucket!.cursor >= 0 &&
    bucket!.cursor < bucket!.order.length;

  if (!cursorUsable) {
    const previousLast = orderUsable ? bucket!.order[bucket!.order.length - 1] : null;
    bucket = { order: reshuffle(ids, previousLast, rng), cursor: 0 };
  }

  const factId = bucket.order[bucket.cursor];
  return {
    factId,
    state: { ...(state ?? {}), [key]: { order: bucket.order, cursor: bucket.cursor + 1 } },
  };
}
